// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Chip, Group, Select, Stack } from '@mantine/core';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { EligibilityAnswers } from './answers';
import { useIntakeV3Answers } from './answers';
import { OTHER_STATE, SERVED_STATES, UNSERVED_STATES } from './eligibility';
import { Note } from './IntakeV3Controls';
import { IntakeV3Frame } from './IntakeV3Frame';
import { ArrowIcon, PinIcon } from './icons';
import { Question } from './Question';
import type { IntakeV3StepProps } from './steps';
import { getIntakeV3StepPath, INTAKE_V3_WAITLISTS } from './steps';

const STATE_OPTIONS: { value: NonNullable<EligibilityAnswers['state']>; label: string }[] = [
  ...SERVED_STATES.map((state) => ({ value: state.code, label: state.label })),
  { value: OTHER_STATE, label: 'Another state' },
];

/** First v3 step: where the visit happens. Out-of-area patients leave the flow for a waitlist. */
export function EligibilityStep({ step, onContinue }: IntakeV3StepProps): JSX.Element {
  const navigate = useNavigate();
  const { answers, updateAnswers } = useIntakeV3Answers();
  const { state, otherState } = answers.eligibility;
  const [attempted, setAttempted] = useState(false);
  const isOtherState = state === OTHER_STATE;

  const handleContinue = (): void => {
    setAttempted(true);
    if (isOtherState) {
      if (otherState) {
        navigate(getIntakeV3StepPath(INTAKE_V3_WAITLISTS.outOfArea));
      }
      return;
    }
    if (state) {
      onContinue();
    }
  };

  return (
    <IntakeV3Frame
      currentSection={step.section}
      title={step.title}
      description="Three quick answers, before any appointment time is held."
      actions={
        <Button rightSection={isOtherState ? undefined : <ArrowIcon />} onClick={handleContinue}>
          {isOtherState ? 'Join the waitlist' : 'Continue'}
        </Button>
      }
    >
      <Question label="State" variant="section" error={attempted && !state ? 'Choose a state' : null}>
        <Chip.Group
          multiple={false}
          value={state ?? null}
          onChange={(value: string) =>
            updateAnswers('eligibility', { state: value as EligibilityAnswers['state'], otherState: undefined })
          }
        >
          <Group gap={8}>
            {STATE_OPTIONS.map((option) => (
              <Chip key={option.value} value={option.value} variant="outline">
                {option.label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
        {isOtherState && (
          <Stack gap={12} mt={12}>
            <Select
              label="Which state?"
              placeholder="Choose your state"
              data={UNSERVED_STATES}
              searchable
              value={otherState ?? null}
              onChange={(value: string | null) => updateAnswers('eligibility', { otherState: value ?? undefined })}
              error={attempted && !otherState ? 'Choose a state' : undefined}
            />
            <Note tone="orange" icon={<PinIcon />}>
              We're not licensed there yet. Leave your name and email and we'll tell you the day we open.
            </Note>
          </Stack>
        )}
      </Question>
    </IntakeV3Frame>
  );
}
